import React, { useMemo, useState } from "react";
import { DateTime } from "luxon";
import useStore from "../../../../context/useStore";
import useManagers from "../../../../hooks/useManagers/useManagers";
import {
  construireGroupesProchaineTache,
  OPTION_TACHES_TERMINEES,
} from "../../../../helpers/leadTasksOrder";
import useLeadAutoFilters from "./useLeadAutoFilters";
import {
  AGENT_OPTIONS,
  DEVIS_OPTIONS,
  ETAPE_OPTIONS,
  PERIOD_SHORTCUTS_AUTO,
  activeAgentOption,
  activeDevisOption,
  activeEtapeOption,
  statusLabel,
} from "./leadAutoPresets";

const initials = (fullName = "") =>
  fullName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

// Tiroir avancé de la cartouche auto : Statuts, Prochaine tâche, Étape
// (4 états : tous, en cours, estimés, finalisés), Assigné à, Période. Pas de Produits (fixé AUTO) ni de Société
// partenaire.
const LeadAutoAdvancedFiltersPanel = ({
  onClose,
  onExport,
  isExporting,
  isLoading,
}) => {
  const [state] = useStore();
  const { managers } = useManagers();
  const { leadAutoFilters, actions, showExpiredManagers } = useLeadAutoFilters();
  const { updateFilter, updateFilters, toggleShowExpiredManagers } = actions;
  const { LEAD_STATUS, LEAD_TASKS, LEAD_TASK_DEFAULT_ORDER, ROLES_PER_BO } =
    state.constants.items;
  const { statuses, assignedTo, beginAt, endAt, prochaineTache } =
    leadAutoFilters;
  // Option du segment « Étape » correspondant aux filtres courants (PER-2428).
  const etapeActive = activeEtapeOption(leadAutoFilters);
  // Option du segment « Agent » (PER-2433).
  const agentActive = activeAgentOption(leadAutoFilters);
  const devisActive = activeDevisOption(leadAutoFilters);

  const [roleTab, setRoleTab] = useState("ALL");
  const [personQuery, setPersonQuery] = useState("");

  const groupesProchaineTache = construireGroupesProchaineTache(
    LEAD_TASKS,
    LEAD_TASK_DEFAULT_ORDER,
    ["AUTO"],
    prochaineTache,
  );

  const toggleInArray = (name, value, current) => {
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    updateFilter(name, next);
  };

  // --- assignation : rôle + expiration + recherche ---------------------
  const enrichedManagers = useMemo(
    () =>
      managers
        .filter((m) => m.roles && m.roles.length > 0)
        .map((m) => ({
          ...m,
          roleKey: m.roles[0].roleKey,
          isExpired:
            m.contractEndAt &&
            DateTime.fromISO(m.contractEndAt) < DateTime.now(),
        })),
    [managers],
  );

  const roleTabs = [
    { id: "ALL", label: "Tous" },
    ...ROLES_PER_BO.map((r) => ({ id: r.id, label: r.name })),
  ];

  const visiblePeople = enrichedManagers
    .filter((m) => (roleTab === "ALL" ? true : m.roleKey === roleTab))
    .filter((m) => (showExpiredManagers ? true : !m.isExpired))
    .filter((m) =>
      personQuery
        ? m.fullName.toLowerCase().includes(personQuery.toLowerCase())
        : true,
    )
    .sort((a, b) => a.fullName.localeCompare(b.fullName));

  const hiddenExpiredCount = enrichedManagers.filter(
    (m) => m.isExpired && (roleTab === "ALL" ? true : m.roleKey === roleTab),
  ).length;

  return (
    <div className="lf-panel">
      <div className="lf-panel-grid">
        {/* Statuts */}
        <div className="lf-field">
          <label>Statuts</label>
          <div className="lf-statgrid" role="group" aria-label="Statuts">
            {LEAD_STATUS.map((s) => {
              const on = statuses.includes(s.id);
              return (
                <button
                  key={s.id}
                  type="button"
                  className={"lf-opt" + (on ? " lf-on" : "")}
                  aria-pressed={on}
                  onClick={() => toggleInArray("statuses", s.id, statuses)}
                >
                  <i className={`fa fa-${s.icon}`} />
                  <span>{statusLabel(s)}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Prochaine tâche (PER-2409) */}
        <div className="lf-field">
          <label htmlFor="lead-auto-prochaine-tache">Prochaine tâche</label>
          <select
            id="lead-auto-prochaine-tache"
            className="lf-select"
            name="prochaineTache"
            value={prochaineTache}
            onChange={(e) => updateFilter("prochaineTache", e.target.value)}
          >
            <option value="">Toutes…</option>
            {groupesProchaineTache.length > 1
              ? groupesProchaineTache.map((g) => (
                  <optgroup key={g.label} label={g.label}>
                    {g.taches.map((t) => (
                      <option key={`${g.label}-${t.id}`} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </optgroup>
                ))
              : (groupesProchaineTache[0]?.taches || []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
            <option value={OPTION_TACHES_TERMINEES.id}>
              {OPTION_TACHES_TERMINEES.name}
            </option>
          </select>
        </div>

        {/* Étape du formulaire — 4 états (PER-2428), combinaison de
            isEstimated et isFinalized côté graal */}
        <div>
          <div className="lf-field">
            <label>Étape</label>
            <div className="lf-seg" role="group" aria-label="Étape du formulaire">
              {ETAPE_OPTIONS.map((o) => {
                const on = etapeActive?.id === o.id;
                return (
                  <button
                    key={o.id}
                    type="button"
                    className={on ? "lf-on" : ""}
                    aria-pressed={on}
                    onClick={() => updateFilters(o.patch)}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="lf-field mt-3">
            <label>Devis</label>
            <div className="lf-seg" role="group" aria-label="Devis déposé">
              {DEVIS_OPTIONS.map((o) => {
                const on = devisActive?.id === o.id;
                return (
                  <button
                    key={o.id}
                    type="button"
                    className={on ? "lf-on" : ""}
                    aria-pressed={on}
                    onClick={() => updateFilters(o.patch)}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Agent de tarification (PER-2433) : état porté par graal. Depuis PER-2498, c'est
            l'état le plus urgent parmi les compagnies (AssurAli, Allianz (Prima)…) : le
            libellé le dit, le filtre lui-même ne change pas (PER-2500). */}
        <div className="lf-field">
          <label title="État le plus urgent parmi les compagnies sollicitées par l'agent">Agent · toutes compagnies</label>
          <div className="lf-seg" role="group" aria-label="État de l'agent de tarification, le plus urgent parmi les compagnies">
            {AGENT_OPTIONS.map((o) => {
              const on = agentActive?.id === o.id;
              return (
                <button
                  key={o.id}
                  type="button"
                  className={on ? "lf-on" : ""}
                  aria-pressed={on}
                  onClick={() => updateFilters(o.patch)}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Assigné à */}
        <div className="lf-field lf-assignee">
          <label>Assigné à</label>
          <div className="lf-asg-head">
            <div className="lf-roletabs" role="group" aria-label="Filtrer par rôle">
              {roleTabs.map((rt) => (
                <button
                  key={rt.id}
                  type="button"
                  aria-pressed={roleTab === rt.id}
                  className={roleTab === rt.id ? "lf-on" : ""}
                  onClick={() => setRoleTab(rt.id)}
                >
                  {rt.label}
                </button>
              ))}
            </div>
            <div className="lf-asg-search">
              <i className="fa fa-search" />
              <input
                type="text"
                value={personQuery}
                placeholder="Rechercher une personne…"
                aria-label="Rechercher une personne"
                onChange={(e) => setPersonQuery(e.target.value)}
              />
            </div>
          </div>
          <div className="lf-people">
            {visiblePeople.length === 0 && (
              <span className="lf-empty">Aucune personne</span>
            )}
            {visiblePeople.map((m) => {
              const on = assignedTo.includes(m.id);
              return (
                <button
                  key={m.id}
                  type="button"
                  className={"lf-person" + (on ? " lf-on" : "")}
                  aria-pressed={on}
                  onClick={() => toggleInArray("assignedTo", m.id, assignedTo)}
                  title={m.isExpired ? "Contrat expiré" : undefined}
                >
                  <span className="lf-av">{initials(m.fullName)}</span>
                  {m.fullName}
                  {on && (
                    <i
                      className="fa fa-times"
                      style={{ fontSize: 9, marginLeft: 2 }}
                    />
                  )}
                </button>
              );
            })}
            {!showExpiredManagers && hiddenExpiredCount > 0 && (
              <button
                type="button"
                className="lf-expired-link"
                onClick={toggleShowExpiredManagers}
              >
                +{hiddenExpiredCount} expiré(s)
              </button>
            )}
          </div>
        </div>

        {/* Période — valeurs déjà en YYYY-MM-DD, contrat graal */}
        <div className="lf-field">
          <label>Période</label>
          <div className="lf-daterow">
            <input
              type="date"
              className="lf-dateinput"
              value={beginAt}
              aria-label="Début de période"
              onChange={(e) => updateFilter("beginAt", e.target.value)}
            />
            <span className="lf-arrow">→</span>
            <input
              type="date"
              className="lf-dateinput"
              value={endAt}
              aria-label="Fin de période"
              onChange={(e) => updateFilter("endAt", e.target.value)}
            />
          </div>
          <div className="lf-quick-dates">
            {PERIOD_SHORTCUTS_AUTO.map((sc) => (
              <button
                key={sc.id}
                type="button"
                className="lf-qd"
                onClick={() => actions.setPeriodShortcut(sc.id)}
              >
                {sc.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="lf-panel-foot">
        <button
          type="button"
          className="lf-link"
          onClick={actions.clearAllFilters}
        >
          <i className="fa fa-refresh" />
          Réinitialiser
        </button>
        <button
          type="button"
          className="lf-link"
          onClick={onExport}
          disabled={isExporting}
        >
          <i className="fa fa-download" />
          Exporter tout (JSON)
        </button>
        <span className="lf-right">
          <button type="button" className="lf-btn lf-btn-ghost" onClick={onClose}>
            Fermer
          </button>
          <button
            type="submit"
            className="lf-btn lf-btn-primary"
            disabled={isLoading}
          >
            <i className={isLoading ? "fa fa-spinner fa-spin" : "fa fa-search"} />
            Rechercher
          </button>
        </span>
      </div>
    </div>
  );
};

export default LeadAutoAdvancedFiltersPanel;
